import React, { useState } from "react";
import styles from "pages/Login/Login.module.scss";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useParams, useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { useMask } from "@react-input/mask";
import Button, { ButtonWrapper } from "components/Button/Button";
import {
  FormControl,
  FormFeedback,
  FormLabel,
  FormRow,
  FormStep,
  FormField,
  FormTerms,
} from "components/Form/Form";
import BulletProofPasswordWithConfirm from "./BulletProofPasswordWithConfirm";
import Dialog, {
  DialogDescription,
  DialogTitle,
  DialogFooter
} from "components/Dialog/Dialog";
import { checkCpf, checkEmail, createUser } from "../../../services";
import { getSupportUrl } from "utils/getSupportUrl";

const cpfSchema = Yup.object().shape({
  cpf: Yup.string()
    .required("Campo obrigatório")
    .test("cpf", "CPF inválido", (value) => {
      const digits = value.replace(/\D/g, "");
      const sum = [10, 9, 8, 7, 6, 5, 4, 3, 2].reduce((acc, weight, index) => {
        acc += digits[index] * weight;
        return acc;
      }, 0);
      const remainder = sum % 11;
      const dv1 = remainder < 2 ? 0 : 11 - remainder;
      const sum2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2].reduce(
        (acc, weight, index) => {
          acc += digits[index] * weight;
          return acc;
        },
        0
      );
      const remainder2 = sum2 % 11;
      const dv2 = remainder2 < 2 ? 0 : 11 - remainder2;
      return digits[9] == dv1 && digits[10] == dv2;
    }),
});

const nameSchema = Yup.object().shape({
  name: Yup.string().required("Campo obrigatório"),
});

const emailSchema = Yup.object().shape({
  email: Yup.string().email("E-mail inválido").required("Campo obrigatório"),
  confirmEmail: Yup.string()
    .oneOf([Yup.ref("email"), null], "E-mails não coincidem")
    .email("E-mail inválido")
    .required("Campo obrigatório"),
});

const cellPhoneSchema = Yup.object().shape({
  cellPhone: Yup.string()
    .required("Campo obrigatório")
    .matches(/^\([1-9]{2}\) [9]{1}[0-9]{4}-[0-9]{4}$/, "Telefone inválido"),
});

const termsSchema = Yup.object().shape({
  terms: Yup.string().required("É necessário aceitar os termos."),
});

export default function TeacherSignUp() {
  const [formValues, setFormValues] = useState({});
  const [showErrorCpf, setShowErrorCpf] = useState(false);
  const [showCpfFound, setShowCpfFound] = useState(false);
  const [errorAttributes, setErrorAttributes] = useState({});
  const appName = process.env.REACT_APP_NAME;
  const [showEmailErrorDialog, setShowEmailErrorDialog] = useState(false);
  const [emailErrorAttributes, setEmailErrorAttributes] = useState(null);

  const { formStep } = useParams();
  const navigate = useNavigate();
  const cfpRef = useMask({
    mask: "___.___.___-__",
    replacement: { _: /\d/ },
  });
  const cellPhoneRef = useMask({
    mask: "(__) _____-____",
    replacement: { _: /\d/ },
  });

  function handleFormValues(values) {
    setFormValues({ ...formValues, ...values });
  }

  const removeCpfMask = (cpf) => {
    return cpf.replace(/\D/g, "");
  };

  const handleNext = async (values, actions) => {
    handleFormValues(values);
    if (actions) actions.setSubmitting(false);

    if (!formStep) {
      const cpfWithoutMask = removeCpfMask(values.cpf);
      const { status, data } = await checkCpf(cpfWithoutMask);

      if (status === 200) {
        toast.success("CPF validado. Siga preenchendo os dados");
        const dataAttributes = data.data.attributes;
        const newValues = { ...values, ...dataAttributes };
        handleFormValues(newValues);
        navigate("/criar-uma-conta/professor/nome");
      } else if (status === 404) {
        setShowErrorCpf(true);
        setErrorAttributes(data?.errors[0]?.options?.options);
      } else if (status === 422) {
        setShowCpfFound(true);
        setErrorAttributes(data?.errors[0]?.options?.options);
      } else {
        toast.error("Erro ao validar CPF. Tente novamente mais tarde");
        return;
      }
    }
    if (formStep === "nome") {
      navigate("/criar-uma-conta/professor/email");
    }

    if (formStep === "email") {
      const form_email = values.email;
      const {data, status} = await checkEmail(form_email);

      if (status === 200) {
          const {found, email } = data;
          console.log(data)
          if (found) {
            setEmailErrorAttributes({
              email
            });
            setShowEmailErrorDialog(true);

            actions.setFieldTouched("email", true);
            actions.setFieldError("email", "Esse email está atrelado a outro CPF (ou a nenhum). Entre em contato com o suporte.");
            return;
        }
        navigate("/criar-uma-conta/professor/senha");
      } else {
          toast.error("Erro ao validar email. Tente novamente mais tarde");
          return;
      }
    }

    if (formStep === "senha") {
      navigate("/criar-uma-conta/professor/telefone");
    }
    if (formStep === "telefone") {
      navigate("/criar-uma-conta/professor/termos");
    }
    if (formStep === "termos") {
      const { cpf, name, email, password } = formValues;
      const response = await createUser({ data: { attributes: {
            cpf,
            name,
            email,
            password,
          },
        },
      });

      if (response.status === 201 || response.status === 200) {
        toast.success("Conta criada com sucesso");
        navigate("/criar-uma-conta/professor/conta-criada");
      } else {
        toast.error("Erro ao criar conta. Tente novamente mais tarde");
      }
    }
  };

  return (
    <>
      <FormStep show={!formStep}>
        <Formik
          initialValues={{ cpf: formValues.cpf ?? "" }}
          validationSchema={cpfSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={styles.body}>
                <div className={styles.textGroup}>
                  <h1 className={styles.title}>
                    Vamos consultar seus dados na Rede SEDUC
                  </h1>
                </div>

                <FormRow>
                  <FormLabel htmlFor="cpf">*Digite seu CPF</FormLabel>
                  <FormControl
                    type="text"
                    name="cpf"
                    id="cpf"
                    defaultValue={props.values.cpf}
                    invalid={props.errors.cpf && props.touched.cpf}
                    placeholder="000.000.000-00"
                    ref={cfpRef}
                  />
                  <ErrorMessage name="cpf">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                </FormRow>

                <Dialog
                  show={showErrorCpf}
                  onClose={() => setShowErrorCpf(false)}
                >
                  <DialogTitle>
                    Validação de CPF necessária: entre em contato com a escola.
                  </DialogTitle>
                  <DialogDescription>
                    Caso o seu CPF não seja reconhecido, mesmo digitando corretamente,
                    pode ser que ele ainda não esteja validado no sistema.
                    <strong> Procure a secretaria da sua escola para resolver essa questão. </strong>
                  </DialogDescription>
                </Dialog>

                <Dialog
                  show={showCpfFound}
                  onClose={() => setShowCpfFound(false)}
                >
                  {console.log("errorAttributes", errorAttributes)}
                  <DialogTitle>
                  Parece que você já tem uma conta de {errorAttributes?.profile_name}
                  </DialogTitle>
                  <DialogDescription>
                    <p>Use o e-mail cadastrado {errorAttributes?.email} para entrar
                    na plataforma.</p>
                  </DialogDescription>

                  <Button modifiers={["primary"]} anchor to="/entrar">
                    Entrar como {errorAttributes?.profile_name}
                  </Button>
                  <DialogFooter>
                    Se você não é um {errorAttributes?.profile_name},{" "}
                    <a
                      href={getSupportUrl(appName)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      clique aqui para falar com o suporte.
                    </a>
                  </DialogFooter>
                </Dialog>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={["outline"]}
                  onClick={() => navigate("/login")}
                  style={{ minWidth: 134 }}
                >
                  Voltar
                </Button>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  disabled={props.isSubmitting || !props.values.cpf}
                >
                  Consultar
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "nome"}>
        <Formik
          initialValues={{ cpf: formValues.cpf, name: formValues.name ?? "" }}
          validationSchema={nameSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={styles.body}>
                <div className={styles.textGroup}>
                  <h1 className={styles.title}>
                    Confira seus e faça as correções necessárias. Quando
                    terminar, clique em “Próximo”.
                  </h1>
                </div>

                <FormRow disabled={true}>
                  <FormLabel htmlFor="cpf">CPF</FormLabel>
                  <FormControl
                    id="cpf"
                    name="cpf"
                    type="text"
                    value={props.values.cpf}
                  />
                </FormRow>
                <FormRow>
                  <FormLabel htmlFor="name">*Nome</FormLabel>
                  <FormControl
                    id="name"
                    name="name"
                    type="text"
                    value={props.values.name}
                    placeholder="Insira seu nome completo"
                  />
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={["outline"]}
                  onClick={() => navigate("/criar-uma-conta/professor")}
                  style={{ minWidth: 134 }}
                >
                  Voltar
                </Button>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={props.isSubmitting || !props.values.name}
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "email"}>
        <Formik
          initialValues={{
            email: formValues.email ?? "",
            confirmEmail: formValues.email ?? "",
          }}
          validationSchema={emailSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={styles.body}>
                <div className={styles.textGroup}>
                  <h1 className={styles.title}>Preencha com seu email</h1>
                  <p className={`${styles.description} ${styles.box} `}>
                    Toda a comunicação da plataforma será feita pelo email que
                    você cadastrar. Anote-o para não esquecer.
                  </p>
                </div>

                <FormRow>
                  <FormLabel htmlFor="email">*Email</FormLabel>
                  <FormControl
                    id="email"
                    name="email"
                    type="email"
                    value={props.values.email}
                    invalid={props.errors.email && props.touched.email}
                    placeholder="Insira seu e-mail"
                  />
                  <ErrorMessage name="email">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                </FormRow>

                <FormRow>
                  <FormLabel htmlFor="confirmEmail">
                    *Confirme seu email
                  </FormLabel>
                  <FormControl
                    id="confirmEmail"
                    name="confirmEmail"
                    type="email"
                    value={props.values.confirmEmail}
                    invalid={
                      props.errors.confirmEmail && props.touched.confirmEmail
                    }
                    placeholder="Insira seu e-mail"
                  />
                  <ErrorMessage name="confirmEmail">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                </FormRow>
              </div>
              {console.log("emailErrorAttributes", emailErrorAttributes)}
              <Dialog show={showEmailErrorDialog} onClose={() => setShowEmailErrorDialog(false)}>
                <DialogTitle>
                  Parece que você já tem uma conta!
                </DialogTitle>

                <DialogDescription>
                  <p>Entre com <strong>{emailErrorAttributes?.email}</strong> para continuar.</p>
                </DialogDescription>

                <Button modifiers={["primary"]} anchor to="/entrar">
                  Fazer login
                </Button>

                <DialogFooter>
                  Esqueceu sua senha?{" "} <Link to="/esqueci-a-senha">Redefinir senha</Link>.
                </DialogFooter>
              </Dialog>

              <ButtonWrapper>
                <Button
                  modifiers={["outline"]}
                  onClick={() => navigate("/criar-uma-conta/professor/nome")}
                  style={{ minWidth: 134 }}
                >
                  Voltar
                </Button>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={
                    props.isSubmitting ||
                    !props.values.email ||
                    !props.values.confirmEmail
                  }
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

        {/* STEP: SENHA (REPLACED) */}
        <FormStep show={formStep === "senha"}>
        {/**
         * Instead of Formik here, we just use a simple container + bulletproof password input.
         * We'll store the password in formValues via handleNext.
         */}
            <div className={styles.body}>
          <div className={styles.textGroup}>
            <h1 className={styles.title}>Crie uma senha para cadastro.</h1>
          </div>

                {/* State to track if password+confirm is valid so we can enable "Próximo" */}
                {/* Or you can store it in local state if you prefer. */}
                <BulletProofPasswordStep
                    defaultPassword={formValues.password ?? ""}
                    onNext={(password, confirm) => {
                        handleNext({password, confirmPassword: confirm}, null);
                    }}
                    onBack={() => navigate("/criar-uma-conta/professor/email")}
                />
        </div>
      </FormStep>

      <FormStep show={formStep === "telefone"}>
        <Formik
          initialValues={{ cellPhone: formValues.phone ?? "" }}
          validationSchema={cellPhoneSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={styles.body}>
                <div className={styles.textGroup}>
                  <h1 className={styles.title}>
                    Insira o número do seu telefone celular
                  </h1>
                </div>

                <FormRow>
                  <FormLabel htmlFor="cellPhone">Celular com DDD</FormLabel>
                  <FormControl
                    id="cellPhone"
                    name="cellPhone"
                    type="tel"
                    value={props.values.cellPhone}
                    invalid={props.errors.cellPhone && props.touched.cellPhone}
                    placeholder="(00) 00000-0000"
                    ref={cellPhoneRef}
                  />
                  <ErrorMessage name="cellPhone">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={["outline"]}
                  onClick={() => navigate("/criar-uma-conta/professor/senha")}
                  style={{ minWidth: 134 }}
                >
                  Voltar
                </Button>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={props.isSubmitting || !props.values.cellPhone}
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "termos"}>
        <Formik
          initialValues={{ terms: true }}
          validationSchema={termsSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={styles.body}>
                <div className={styles.textGroup}>
                  <h1 className={styles.title}>
                    Termos de uso e política de privacidade
                  </h1>
                </div>

                <FormRow>
                  <FormTerms />
                </FormRow>

                <FormRow>
                  <FormField round>
                    <Field type="checkbox" name="terms" />
                    <span>
                      Ao clicar em “Criar conta”, você concorda com os{" "}
                      <Link to="/">termos de uso</Link> e{" "}
                      <Link to="/">política de privacidade</Link>
                    </span>
                  </FormField>
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={["outline"]}
                  onClick={() =>
                    navigate("/criar-uma-conta/professor/telefone")
                  }
                  style={{ minWidth: 134 }}
                >
                  Voltar
                </Button>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={props.isSubmitting || !props.values.terms}
                >
                  Criar minha conta
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "conta-criada"}>
        <div className={styles.body}>
          <div className={styles.textGroup}>
            <h1 className={styles.title}>Conta foi criada!</h1>
            <p className={styles.description}>
              Enviamos um email para você. Acesse-o e ative sua conta.
            </p>
          </div>
        </div>
      </FormStep>
    </>
  );
}

/**
 * A tiny wrapper to show bulletproof password+confirm and 2 buttons: "Voltar" & "Próximo"
 * - We store the password in local state, track validity, then pass it up
 *   when user clicks "Próximo".
 */
function BulletProofPasswordStep({defaultPassword, onNext, onBack}) {
    const [isValid, setIsValid] = useState(false);
    const [pwd, setPwd] = useState(defaultPassword || "");
    const [confirmPwd, setConfirmPwd] = useState("");

    return (
        <>
      <BulletProofPasswordWithConfirm
          onValidChange={(valid) => setIsValid(valid)}
          onPasswordsChange={(password, confirm) => {
              setPwd(password);
              setConfirmPwd(confirm);
          }}
      />

      <ButtonWrapper style={{marginTop: 16}}>
        <Button
            modifiers={["outline"]}
            onClick={onBack}
            style={{minWidth: 134}}
        >
          Voltar
        </Button>
        <Button
            modifiers={["primary"]}
            style={{minWidth: 134}}
            // Only allow next if password is valid
            onClick={() => onNext(pwd, confirmPwd)}
            disabled={!isValid}
        >
          Próximo
        </Button>
      </ButtonWrapper>
    </>
    );
}
